import { useEffect, useState } from "react";
import { fetchPage } from "../lib/api";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { ChevronDown, ChevronUp, Phone, MapPin, CheckCircle2, Clock, BadgePoundSterling } from "lucide-react";
import IntensiveEnquireModal from "../components/IntensiveEnquireModal";

// ─── Simple SVG map of Southampton + Winchester test centres ───────────────
function TestCentreMap() {
  // Rough geographic layout: Winchester is NNW of Southampton (~15km).
  // Map is schematic, not geographically precise.
  return (
    <svg
      viewBox="0 0 400 320"
      className="w-full h-auto"
      role="img"
      aria-label="Southampton and Winchester DVSA test centres"
      data-testid="test-centre-map"
    >
      {/* Background */}
      <rect x="0" y="0" width="400" height="320" fill="#F0F9EE" />
      {/* Rolling land shapes */}
      <path d="M0 180 Q100 140 200 170 T400 150 L400 320 L0 320 Z" fill="#E8F3E4" />
      <path d="M0 230 Q120 200 220 230 T400 220 L400 320 L0 320 Z" fill="#DCEBD5" />
      {/* Southampton Water (coastline) */}
      <path d="M60 290 Q160 260 300 300 L400 300 L400 320 L0 320 L0 300 Z" fill="#B6D8EA" />
      {/* Road from Winchester to Southampton (M3-style) */}
      <path
        d="M190 75 Q220 160 230 240"
        stroke="#C0CFC0"
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M190 75 Q220 160 230 240"
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeDasharray="6 6"
        fill="none"
      />
      {/* Road label */}
      <text x="238" y="155" fontSize="10" fill="#4B5563" fontFamily="system-ui, sans-serif">
        M3
      </text>

      {/* Winchester pin */}
      <g>
        <circle cx="190" cy="75" r="18" fill="#FFFFFF" stroke="#37B160" strokeWidth="2" />
        <circle cx="190" cy="75" r="10" fill="#37B160" />
        <path d="M190 93 L185 102 L195 102 Z" fill="#37B160" />
        <text
          x="214"
          y="68"
          fontSize="13"
          fill="#0A0F00"
          fontFamily="Outfit, system-ui, sans-serif"
          fontWeight="600"
        >
          Winchester
        </text>
        <text x="214" y="83" fontSize="10" fill="#4B5563" fontFamily="system-ui, sans-serif">
          SO23 9SY
        </text>
      </g>

      {/* Southampton pin */}
      <g>
        <circle cx="230" cy="240" r="18" fill="#FFFFFF" stroke="#0A0F00" strokeWidth="2" />
        <circle cx="230" cy="240" r="10" fill="#0A0F00" />
        <path d="M230 258 L225 267 L235 267 Z" fill="#0A0F00" />
        <text
          x="254"
          y="236"
          fontSize="13"
          fill="#0A0F00"
          fontFamily="Outfit, system-ui, sans-serif"
          fontWeight="600"
        >
          Southampton
        </text>
        <text x="254" y="251" fontSize="10" fill="#4B5563" fontFamily="system-ui, sans-serif">
          SO16 9FP
        </text>
      </g>

      {/* Compass */}
      <g transform="translate(360, 40)">
        <text x="0" y="0" fontSize="10" fill="#9CA3AF" fontFamily="system-ui, sans-serif" textAnchor="middle">
          N
        </text>
        <path d="M0 4 L-4 14 L0 10 L4 14 Z" fill="#9CA3AF" />
      </g>
    </svg>
  );
}

// ─── Course card with expand/read-more ─────────────────────────────────────
function CourseCard({ course, onEnquire }) {
  const [open, setOpen] = useState(false);
  const [tpOpen, setTpOpen] = useState(false);
  const theory = course.theory_test_pro;

  return (
    <article
      className="bg-white rounded-2xl border border-black/5 overflow-hidden transition-all duration-300 hover:shadow-lg"
      data-testid={`intensive-course-${course.key}`}
    >
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h3
              className="text-xl md:text-2xl font-medium text-[#0A0F00]"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid={`intensive-course-name-${course.key}`}
            >
              {course.name}
            </h3>
            <div className="flex items-center gap-3 mt-1.5 text-sm text-[#4B5563]">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {course.hours}
              </span>
              <span className="inline-flex items-center gap-1 font-semibold text-[#0A0F00]">
                <BadgePoundSterling className="h-3.5 w-3.5" />
                {course.price}
              </span>
            </div>
          </div>
          <Button
            onClick={() => onEnquire(course.name)}
            className="bg-[#37B160] hover:bg-[#2D9450] text-white rounded-full"
            data-testid={`intensive-enquire-btn-${course.key}`}
          >
            Enquire now
          </Button>
        </div>

        {course.best_for && (
          <div className="mt-5 inline-block">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#15803D] bg-[#DCFCE7] px-2.5 py-1 rounded-full">
              Best for
            </span>
            <p className="mt-2 text-sm text-[#374151] font-medium">{course.best_for}</p>
          </div>
        )}

        {course.summary && (
          <p className="mt-4 text-[15px] text-[#4B5563] leading-relaxed">{course.summary}</p>
        )}

        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#37B160] hover:text-[#2D9450] transition-colors"
          data-testid={`intensive-course-toggle-${course.key}`}
          aria-expanded={open}
        >
          {open ? (
            <>
              Read less <ChevronUp className="h-4 w-4" />
            </>
          ) : (
            <>
              Read more <ChevronDown className="h-4 w-4" />
            </>
          )}
        </button>

        {open && (
          <div
            className="mt-4 pt-4 border-t border-black/5 text-[15px] text-[#374151] leading-relaxed"
            data-testid={`intensive-course-body-${course.key}`}
          >
            <p>{course.body}</p>
          </div>
        )}
      </div>

      {theory && theory.included && (
        <div className="border-t border-[#DCFCE7] bg-gradient-to-br from-[#F0FDF4] to-white px-6 md:px-8 py-5">
          <button
            type="button"
            onClick={() => setTpOpen(o => !o)}
            className="w-full text-left group"
            data-testid={`intensive-tpro-toggle-${course.key}`}
            aria-expanded={tpOpen}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#15803D]">
                  Included free
                </div>
                <h4
                  className="mt-1 text-[17px] font-medium text-[#0A0F00] group-hover:text-[#15803D] transition-colors"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {theory.title}
                </h4>
                <p className="mt-1 text-sm text-[#4B5563]">{theory.subtitle}</p>
              </div>
              <div className="flex-none mt-1">
                {tpOpen ? (
                  <ChevronUp className="h-5 w-5 text-[#37B160]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#37B160]" />
                )}
              </div>
            </div>
          </button>

          {tpOpen && (
            <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid={`intensive-tpro-benefits-${course.key}`}>
              {(theory.benefits || []).map((b, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-[#37B160] mt-0.5 flex-none" />
                  <div className="text-sm">
                    <div className="font-medium text-[#0A0F00]">{b.title}</div>
                    <div className="text-[#4B5563] mt-0.5 leading-snug">{b.body}</div>
                  </div>
                </div>
              ))}
              {theory.url && (
                <div className="sm:col-span-2 pt-2">
                  <a
                    href={theory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#37B160] font-medium underline hover:text-[#2D9450]"
                    data-testid={`intensive-tpro-link-${course.key}`}
                  >
                    Visit Theory Test Pro →
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function IntensiveCourses() {
  const [page, setPage] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeCourse, setActiveCourse] = useState("");

  useEffect(() => {
    fetchPage("packages-bespoke")
      .then(setPage)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const openEnquire = (courseName) => {
    setActiveCourse(courseName);
    setModalOpen(true);
  };

  if (!loaded) {
    return <div className="min-h-[400px] flex items-center justify-center text-[#9CA3AF]">Loading…</div>;
  }

  const sections = page?.sections || [];
  const hero = sections.find(s => s.type === "intensive_hero");
  const centres = sections.find(s => s.type === "test_centres");
  const steps = sections.find(s => s.type === "intensive_steps");
  const courses = sections.find(s => s.type === "intensive_courses");
  const cta = sections.find(s => s.type === "intensive_cta");

  return (
    <>
      <Seo
        title={page?.meta_title || "Intensive Driving Courses — Lime Driving School"}
        description={page?.meta_description}
        robots={page?.meta_robots}
        jsonLd={page?.schema_json}
      />

      {/* HERO */}
      {hero && (
        <section
          className="pt-16 pb-14 md:pt-24 md:pb-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          data-testid="intensive-hero"
        >
          {hero.eyebrow && (
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">
              {hero.eyebrow}
            </span>
          )}
          <h1
            className="mt-3 text-4xl sm:text-5xl md:text-6xl font-medium text-[#0A0F00] tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {hero.title}
          </h1>
          {hero.pass_rate && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#DCFCE7] text-[#15803D] text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4" />
              {hero.pass_rate}
            </div>
          )}
          <p className="mt-6 text-[17px] md:text-lg text-[#4B5563] leading-relaxed max-w-3xl mx-auto">
            {hero.subtitle}
          </p>
          {hero.footnote && (
            <p className="mt-6 text-xs text-[#9CA3AF] italic max-w-2xl mx-auto">{hero.footnote}</p>
          )}
        </section>
      )}

      {/* TEST CENTRES */}
      {centres && (
        <section
          className="pb-16 md:pb-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
          data-testid="intensive-test-centres"
        >
          <div className="grid md:grid-cols-2 gap-10 items-center bg-white rounded-3xl border border-black/5 overflow-hidden">
            <div className="p-8 md:p-10">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">
                Service area
              </span>
              <h2
                className="mt-3 text-2xl md:text-3xl font-medium text-[#0A0F00]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {centres.title}
              </h2>
              <p className="mt-3 text-[15px] text-[#4B5563] leading-relaxed">{centres.subtitle}</p>
              <ul className="mt-6 space-y-3">
                {(centres.centres || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`test-centre-${i}`}>
                    <MapPin className="h-5 w-5 text-[#37B160] mt-0.5 flex-none" />
                    <div>
                      <div className="font-medium text-[#0A0F00]">{c.name}</div>
                      <div className="text-sm text-[#4B5563]">{c.address}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#F0F9EE]">
              <TestCentreMap />
            </div>
          </div>
        </section>
      )}

      {/* STEPS */}
      {steps && (
        <section
          className="pb-16 md:pb-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
          data-testid="intensive-steps"
        >
          <div className="text-center">
            <h2
              className="text-3xl md:text-4xl font-medium text-[#0A0F00]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {steps.title}
            </h2>
            {steps.subtitle && (
              <p className="mt-3 text-[#4B5563]">{steps.subtitle}</p>
            )}
          </div>
          <ol className="mt-10 grid md:grid-cols-3 gap-5">
            {(steps.items || []).map((it, i) => (
              <li
                key={i}
                className="bg-white rounded-2xl border border-black/5 p-6"
                data-testid={`intensive-step-${i}`}
              >
                <div className="h-10 w-10 rounded-full bg-[#DCFCE7] text-[#15803D] font-semibold flex items-center justify-center">
                  {it.step}
                </div>
                <h3
                  className="mt-4 text-lg font-medium text-[#0A0F00]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {it.title}
                </h3>
                {it.body && <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{it.body}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* COURSES */}
      {courses && (
        <section
          className="pb-16 md:pb-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
          data-testid="intensive-courses"
        >
          <div className="text-center">
            <h2
              className="text-3xl md:text-4xl font-medium text-[#0A0F00]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {courses.title}
            </h2>
            {courses.subtitle && (
              <p className="mt-3 text-[#4B5563] max-w-2xl mx-auto">{courses.subtitle}</p>
            )}
          </div>
          <div className="mt-10 space-y-5">
            {(courses.items || []).map((c) => (
              <CourseCard key={c.key} course={c} onEnquire={openEnquire} />
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      {cta && (
        <section
          className="pb-20 md:pb-28 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          data-testid="intensive-cta"
        >
          <h2
            className="text-3xl md:text-4xl font-medium text-[#0A0F00]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {cta.title}
          </h2>
          {cta.subtitle && (
            <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">{cta.subtitle}</p>
          )}
          {cta.phone && (
            <a
              href={`tel:${cta.phone.replace(/\s+/g, "")}`}
              className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-[#37B160] hover:text-[#2D9450] transition-colors"
              data-testid="intensive-cta-phone"
            >
              <Phone className="h-5 w-5" />
              {cta.phone}
            </a>
          )}
        </section>
      )}

      <IntensiveEnquireModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        courseName={activeCourse}
      />
    </>
  );
}
